import React, { useState } from 'react';
import { ChevronDown, ExternalLink, Info, CalendarClock, Sparkles } from 'lucide-react';
import { ACTIONS } from './crmOpportunityActions';

const READINESS_STYLE = {
  'Ready to Contact': 'bg-emerald-50 text-emerald-700 border-emerald-200',
  'Needs Enrichment': 'bg-amber-50 text-amber-700 border-amber-200',
  'No Decision Maker Found': 'bg-gray-100 text-gray-500 border-gray-200',
  'Already Contacted': 'bg-blue-50 text-blue-700 border-blue-200',
};

// Buckets where prior CRM/activity context exists, so the AI follow-up
// drafter (with prior-context) is a useful one-click shortcut.
const FOLLOW_UP_DRAFT_BUCKETS = ['followup_overdue', 'followup_due_today', 'awaiting_response', 'response_needed'];

// Phase 6D — shared opportunity row used by both the "Today" worklist and
// the Priority Queue tab, so actions/behavior never drift between the two.
export const CRMOpportunityCard = ({ opp, onOpenCompany, onOutreach, onRunAction }) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [pickingDate, setPickingDate] = useState(false);
  const [followUpDate, setFollowUpDate] = useState('');

  const canInitialOutreach = opp.contact_readiness === 'Ready to Contact';
  const canDraftFollowUp = !canInitialOutreach && FOLLOW_UP_DRAFT_BUCKETS.includes(opp.bucket) && opp.best_contact;

  const handleAction = (key) => {
    if (key === 'follow_up_later') {
      setPickingDate(true);
      return;
    }
    setMenuOpen(false);
    onRunAction(opp, key);
  };

  const submitFollowUpLater = () => {
    if (!followUpDate) return;
    onRunAction(opp, 'follow_up_later', { date: followUpDate });
    setPickingDate(false);
    setMenuOpen(false);
    setFollowUpDate('');
  };

  return (
    <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-4" data-testid={`opportunity-card-${opp.id}`}>
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <button onClick={() => onOpenCompany(opp.id)} data-testid={`open-company-${opp.id}`}
              className="font-semibold text-[#0A1F44] hover:underline text-sm">{opp.name}</button>
            <span className="text-xs text-gray-400">{[opp.industry, opp.location].filter(Boolean).join(' • ')}</span>
            <span className="px-2 py-0.5 rounded-full text-[10px] font-medium bg-gray-100 text-gray-600 border border-gray-200">{opp.pipeline_stage}</span>
            {opp.opportunity_type === 'expansion' && (
              <span className="px-2 py-0.5 rounded-full text-[10px] font-medium bg-purple-50 text-purple-600 border border-purple-200">Existing Client</span>
            )}
            <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium border ${READINESS_STYLE[opp.contact_readiness]}`}>{opp.contact_readiness}</span>
          </div>
          {opp.signal_summary && <p className="text-xs text-gray-500 mt-1 truncate max-w-xl">{opp.signal_summary}</p>}
          <div className="flex items-center gap-4 text-[11px] text-gray-400 mt-2 flex-wrap">
            <span>Contact: {opp.best_contact ? `${opp.best_contact.name}${opp.best_contact.title ? ` — ${opp.best_contact.title}` : ''}` : 'None on file'}</span>
            {opp.last_activity_at && <span>Last activity: {opp.last_activity_at.slice(0, 10)}</span>}
            {opp.next_action_due_date && <span className="flex items-center gap-1"><CalendarClock className="h-3 w-3" /> Follow-up: {opp.next_action_due_date}</span>}
            {opp.has_agreement && <span>Agreement: {opp.agreement_status}</span>}
            {opp.has_job_order && <span>Job Order: {opp.job_order_status}</span>}
          </div>
        </div>

        <div className="flex items-center gap-2 flex-shrink-0">
          <div className="text-right">
            <div className="flex items-center gap-1 text-sm font-bold text-[#0A1F44]" title={opp.priority_reasons.join('; ')}>
              <Info className="h-3 w-3 text-gray-400" /> {opp.priority_score}
            </div>
            <div className="text-[10px] text-gray-400">priority score</div>
          </div>

          {(canInitialOutreach || canDraftFollowUp) && (
            <button onClick={() => onOutreach(opp, canInitialOutreach ? 'initial' : 'follow_up')}
              data-testid={`prepare-outreach-btn-${opp.id}`}
              className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium rounded-lg bg-[#0A1F44] text-white hover:bg-[#0A1F44]/90">
              <Sparkles className="h-3 w-3 text-[#D4AF37]" /> {canInitialOutreach ? 'Prepare Outreach' : 'Draft Follow-Up'}
            </button>
          )}

          <div className="relative">
            <button onClick={() => setMenuOpen(!menuOpen)} data-testid={`opportunity-actions-btn-${opp.id}`}
              className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium rounded-lg border border-gray-200 text-gray-600 hover:border-[#0A1F44]/40">
              {(!canInitialOutreach && !canDraftFollowUp) ? opp.recommended_action : 'Actions'} <ChevronDown className="h-3 w-3" />
            </button>
            {menuOpen && (
              <div className="absolute right-0 mt-1 w-56 bg-white border border-gray-200 rounded-lg shadow-lg z-20 py-1" data-testid={`opportunity-actions-menu-${opp.id}`}>
                {ACTIONS.map(a => a.key === 'follow_up_later' ? (
                  <div key={a.key} className="px-3 py-1.5">
                    {pickingDate ? (
                      <div className="flex items-center gap-1">
                        <input type="date" value={followUpDate} onChange={e => setFollowUpDate(e.target.value)} data-testid={`followup-date-${opp.id}`}
                          className="text-xs border border-gray-200 rounded px-1 py-0.5 flex-1" />
                        <button onClick={submitFollowUpLater} data-testid={`followup-submit-${opp.id}`} className="text-xs text-[#0A1F44] font-medium">Set</button>
                      </div>
                    ) : (
                      <button onClick={() => setPickingDate(true)} data-testid={`opportunity-action-follow_up_later-${opp.id}`}
                        className="text-xs text-gray-600 hover:text-[#0A1F44] w-full text-left">{a.label}</button>
                    )}
                  </div>
                ) : (
                  <button key={a.key} onClick={() => handleAction(a.key)} data-testid={`opportunity-action-${a.key}-${opp.id}`}
                    className="block w-full text-left px-3 py-1.5 text-xs text-gray-600 hover:bg-gray-50 hover:text-[#0A1F44]">
                    {a.label}
                  </button>
                ))}
              </div>
            )}
          </div>

          <button onClick={() => onOpenCompany(opp.id)} data-testid={`opportunity-open-${opp.id}`}
            className="p-1.5 text-gray-400 hover:text-[#0A1F44]"><ExternalLink className="h-4 w-4" /></button>
        </div>
      </div>
    </div>
  );
};
